"use client";

import {
	AlertTriangle,
	Check,
	Download,
	FileVideo2,
	FlipHorizontal2,
	FlipVertical2,
	FolderOpen,
	Info,
	LoaderCircle,
	RefreshCcw,
	RotateCcw,
	RotateCw,
	ShieldCheck,
	Trash2,
	UploadCloud,
} from "lucide-react";
import {
	type ChangeEvent,
	type DragEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/ui";
import { loadFFmpeg, rotateVideo } from "@/lib/ffmpeg";

type ToolPhase =
	| "empty"
	| "inspecting"
	| "ready"
	| "unsupported"
	| "processing"
	| "success"
	| "failed";
type OutputFormat = "MP4" | "WebM";
type OutputQuality = "Balanced" | "High" | "Smaller file";
type Rotation = 0 | 90 | 180 | 270;

const OUTPUT_FORMATS: readonly OutputFormat[] = ["MP4", "WebM"];
const OUTPUT_QUALITIES: readonly OutputQuality[] = [
	"Balanced",
	"High",
	"Smaller file",
];

function formatBytes({
	bytes,
	locale,
}: {
	bytes: number;
	locale: string;
}): string {
	if (bytes < 1024) return `${bytes} B`;
	const units = ["KB", "MB", "GB"];
	let value = bytes / 1024;
	let unit = units[0];
	for (let index = 1; value >= 1024 && index < units.length; index += 1) {
		value /= 1024;
		unit = units[index];
	}
	return `${new Intl.NumberFormat(locale, {
		maximumFractionDigits: value >= 10 ? 0 : 1,
	}).format(value)} ${unit}`;
}

function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "—";
	const rounded = Math.round(seconds);
	const hours = Math.floor(rounded / 3600);
	const minutes = Math.floor((rounded % 3600) / 60);
	const remainingSeconds = rounded % 60;
	return hours
		? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`
		: `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function getOrientationKey({
	width,
	height,
}: {
	width: number;
	height: number;
}): "unknown" | "square" | "landscape" | "portrait" {
	if (!width || !height) return "unknown";
	if (width === height) return "square";
	return width > height ? "landscape" : "portrait";
}

function wrapRotation(value: number): Rotation {
	const normalized = ((value % 360) + 360) % 360;
	if (normalized === 90 || normalized === 180 || normalized === 270) {
		return normalized;
	}
	return 0;
}

export function RotateVideo() {
	const t = useTranslations("RotateVideo.tool");
	const locale = useLocale();
	const inputRef = useRef<HTMLInputElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const videoUrlRef = useRef<string | null>(null);
	const exportButtonRef = useRef<HTMLButtonElement>(null);
	const [file, setFile] = useState<File | null>(null);
	const [videoUrl, setVideoUrl] = useState<string | null>(null);
	const [phase, setPhase] = useState<ToolPhase>("empty");
	const [error, setError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [duration, setDuration] = useState(0);
	const [sourceSize, setSourceSize] = useState({ width: 0, height: 0 });
	const [rotation, setRotation] = useState<Rotation>(0);
	const [flipHorizontal, setFlipHorizontal] = useState(false);
	const [flipVertical, setFlipVertical] = useState(false);
	const [format, setFormat] = useState<OutputFormat>("MP4");
	const [quality, setQuality] = useState<OutputQuality>("Balanced");
	const [keepAudio, setKeepAudio] = useState(true);
	const [progress, setProgress] = useState(0);
	const [resultBlob, setResultBlob] = useState<Blob | null>(null);
	const [resultUrl, setResultUrl] = useState<string | null>(null);
	const [processingError, setProcessingError] = useState<string | null>(null);

	useEffect(
		() => () => {
			if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
			if (resultUrl) URL.revokeObjectURL(resultUrl);
		},
		[resultUrl],
	);

	const clearPreview = () => {
		if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
		videoUrlRef.current = null;
		setVideoUrl(null);
	};

	const resetTransform = () => {
		setRotation(0);
		setFlipHorizontal(false);
		setFlipVertical(false);
		setProgress(0);
		setResultBlob(null);
		if (resultUrl) {
			URL.revokeObjectURL(resultUrl);
			setResultUrl(null);
		}
		setProcessingError(null);
	};

	const removeFile = () => {
		clearPreview();
		setFile(null);
		setPhase("empty");
		setError(null);
		setDuration(0);
		setSourceSize({ width: 0, height: 0 });
		setProgress(0);
		setResultBlob(null);
		if (resultUrl) {
			URL.revokeObjectURL(resultUrl);
			setResultUrl(null);
		}
		setProcessingError(null);
		resetTransform();
		if (inputRef.current) inputRef.current.value = "";
	};

	const chooseFile = (nextFile?: File) => {
		if (!nextFile) return;
		setError(null);
		if (!nextFile.type.startsWith("video/")) {
			clearPreview();
			setFile(null);
			setPhase("empty");
			setError(t("errors.invalid"));
			if (inputRef.current) inputRef.current.value = "";
			return;
		}

		clearPreview();
		const nextUrl = URL.createObjectURL(nextFile);
		videoUrlRef.current = nextUrl;
		setVideoUrl(nextUrl);
		setFile(nextFile);
		setDuration(0);
		setSourceSize({ width: 0, height: 0 });
		resetTransform();
		setPhase("inspecting");
	};

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		chooseFile(event.target.files?.[0]);
	};
	const openFilePicker = () => {
		if (!inputRef.current) return;
		inputRef.current.value = "";
		inputRef.current.click();
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);
		chooseFile(event.dataTransfer.files[0]);
	};

	const handleMetadata = () => {
		const video = videoRef.current;
		if (
			!video ||
			!Number.isFinite(video.duration) ||
			video.duration <= 0 ||
			!video.videoWidth ||
			!video.videoHeight
		) {
			setPhase("unsupported");
			setError(t("errors.metadata"));
			return;
		}
		setDuration(video.duration);
		setSourceSize({ width: video.videoWidth, height: video.videoHeight });
		setPhase("ready");
	};

	const handleVideoError = () => {
		setPhase("unsupported");
		setError(t("errors.preview"));
	};

	const startRotate = async () => {
		if (!file) return;

		try {
			setPhase("processing");
			setProgress(0);
			setProcessingError(null);

			// Load FFmpeg if not already loaded
			await loadFFmpeg();

			const result = await rotateVideo({
				file,
				options: {
					rotation,
					flipHorizontal,
					flipVertical,
					format,
					quality,
					keepAudio,
				},
				onProgress: (p) => {
					setProgress(p.percent);
				},
			});

			setResultBlob(result);
			const url = URL.createObjectURL(result);
			setResultUrl(url);
			setPhase("success");
		} catch (err) {
			console.error("Rotation failed:", err);
			setPhase("failed");
			setProcessingError(t("errors.processing"));
		}
	};

	const downloadResult = () => {
		if (!resultUrl || !file) return;

		const a = document.createElement("a");
		a.href = resultUrl;
		const originalName = file.name.replace(/\.[^/.]+$/, "");
		a.download = `${originalName}-rotated.${format.toLowerCase()}`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const resetToEditor = () => {
		setPhase("ready");
		setProgress(0);
		if (resultUrl) {
			URL.revokeObjectURL(resultUrl);
			setResultUrl(null);
		}
		setResultBlob(null);
		setProcessingError(null);
	};

	const isQuarterTurn = rotation === 90 || rotation === 270;
	const outputSize = isQuarterTurn
		? { width: sourceSize.height, height: sourceSize.width }
		: sourceSize;
	const outputRatio =
		outputSize.width && outputSize.height
			? outputSize.width / outputSize.height
			: 16 / 9;
	const hasTransform = rotation !== 0 || flipHorizontal || flipVertical;
	const transformSummary = [
		rotation
			? t("summary.clockwise", { degrees: rotation })
			: t("summary.none"),
		flipHorizontal ? t("summary.horizontal") : null,
		flipVertical ? t("summary.vertical") : null,
	]
		.filter(Boolean)
		.join(" · ");
	const previewTransform = `rotate(${rotation}deg) scaleX(${flipHorizontal ? -1 : 1}) scaleY(${flipVertical ? -1 : 1})`;

	return (
		<section
			aria-label={t("aria.workspace")}
			className="mx-auto w-full max-w-[84rem]"
		>
			<div className="overflow-hidden rounded-[2rem] border border-blue-200/80 bg-slate-50/90 shadow-[0_28px_80px_-42px_rgba(37,99,235,0.48)] backdrop-blur-xl dark:border-blue-900/70 dark:bg-slate-950/88 dark:shadow-[0_28px_80px_-42px_rgba(34,211,238,0.25)]">
				<div className="flex flex-col gap-3 border-b border-blue-100 bg-white/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 dark:border-blue-950 dark:bg-slate-950/80">
					<div>
						<h2 className="font-bold tracking-tight">{t("header.title")}</h2>
						<p className="text-muted-foreground mt-0.5 text-sm">
							{t("header.description")}
						</p>
					</div>
					<span className="inline-flex min-h-8 w-fit items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 px-3 text-xs font-semibold text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/50 dark:text-cyan-100">
						<ShieldCheck className="size-3.5" aria-hidden="true" />{" "}
						{t("header.badge")}
					</span>
				</div>

				<div className="p-4 sm:p-6 lg:p-8">
					<input
						ref={inputRef}
						type="file"
						accept="video/*"
						className="hidden"
						onChange={handleInputChange}
					/>
					{!file ? (
						<div
							className={cn(
								"relative flex min-h-80 flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-white/90 px-6 text-center shadow-inner transition-colors dark:bg-slate-900/80",
								isDragging
									? "border-cyan-400 bg-cyan-50/80 dark:border-cyan-400 dark:bg-cyan-950/30"
									: "border-blue-200 hover:border-blue-400 dark:border-blue-900 dark:hover:border-cyan-600",
							)}
							onDragEnter={(event) => {
								event.preventDefault();
								setIsDragging(true);
							}}
							onDragOver={(event) => event.preventDefault()}
							onDragLeave={(event) => {
								if (
									!(event.relatedTarget instanceof Node) ||
									!event.currentTarget.contains(event.relatedTarget)
								) {
									setIsDragging(false);
								}
							}}
							onDrop={handleDrop}
						>
							<div className="flex size-16 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/20 dark:bg-cyan-400 dark:text-slate-950">
								<UploadCloud className="size-7" aria-hidden="true" />
							</div>
							<p className="mt-6 text-2xl font-bold tracking-tight">
								{t("drop.title")}
							</p>
							<Button
								className="mt-6 h-12 rounded-xl bg-blue-600 px-6 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 dark:bg-cyan-400 dark:text-slate-950 dark:hover:bg-cyan-300"
								onClick={openFilePicker}
							>
								<FolderOpen className="size-4.5" aria-hidden="true" />{" "}
								{t("drop.choose")}
							</Button>
							<p className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
								<ShieldCheck
									className="size-3.5 text-cyan-600"
									aria-hidden="true"
								/>
								{t("drop.privacy")}
							</p>
							{error ? (
								<div
									className="mt-5 flex max-w-md items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-start text-sm text-red-900 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-100"
									role="alert"
								>
									<AlertTriangle
										className="mt-0.5 size-4 shrink-0"
										aria-hidden="true"
									/>
									{error}
								</div>
							) : null}
						</div>
					) : (
						<div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(20rem,0.9fr)]">
							<div className="min-w-0 space-y-4">
								<div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm sm:p-5 dark:border-blue-950 dark:bg-slate-900">
									<div className="mb-4 flex items-center justify-between gap-3">
										<div className="flex min-w-0 items-center gap-3">
											<span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-950/70 dark:text-cyan-300">
												<FileVideo2 className="size-5" aria-hidden="true" />
											</span>
											<div className="min-w-0">
												<p className="truncate font-semibold">{file.name}</p>
												<p className="text-muted-foreground mt-0.5 text-xs">
													{formatBytes({ bytes: file.size, locale })}
													{sourceSize.width
														? ` · ${sourceSize.width}×${sourceSize.height}`
														: ""}
													{duration ? ` · ${formatDuration(duration)}` : ""}
												</p>
											</div>
										</div>
										<div className="flex shrink-0 gap-1">
											<Button
												variant="ghost"
												className="h-10 rounded-xl px-3"
												onClick={openFilePicker}
											>
												{t("file.replace")}
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="size-10 rounded-xl"
												onClick={removeFile}
												aria-label={t("aria.remove")}
											>
												<Trash2 className="size-4" aria-hidden="true" />
											</Button>
										</div>
									</div>

									<div className="relative flex min-h-[22rem] items-center justify-center overflow-hidden rounded-2xl bg-slate-200/80 p-4 dark:bg-slate-950 sm:min-h-[28rem]">
										{phase === "inspecting" ? (
											<div
												className="absolute z-10 flex flex-col items-center gap-3 rounded-xl bg-white/90 px-5 py-4 text-sm font-semibold text-slate-700 shadow-lg backdrop-blur dark:bg-slate-900/90 dark:text-slate-200"
												role="status"
											>
												<LoaderCircle
													className="size-5 animate-spin motion-reduce:animate-none"
													aria-hidden="true"
												/>
												{t("file.reading")}
											</div>
										) : null}
										{phase === "unsupported" ? (
											<div
												className="max-w-md rounded-2xl border border-amber-300 bg-amber-50 p-5 text-center text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
												role="alert"
											>
												<AlertTriangle
													className="mx-auto size-6"
													aria-hidden="true"
												/>
												<p className="mt-3 font-semibold">
													{t("file.unavailable")}
												</p>
												<p className="mt-1 text-sm leading-6">{error}</p>
												<Button
													variant="outline"
													className="mt-4 h-11 rounded-xl"
													onClick={openFilePicker}
												>
													{t("file.chooseAnother")}
												</Button>
											</div>
										) : null}
										{videoUrl ? (
											<div
												className={cn(
													"relative max-h-[28rem] max-w-full overflow-hidden rounded-xl bg-black shadow-xl transition-[aspect-ratio,width,height] duration-200 motion-reduce:transition-none",
													phase !== "ready" &&
														phase !== "processing" &&
														phase !== "success" &&
														phase !== "failed" &&
														"invisible",
												)}
												style={{
													aspectRatio: `${outputRatio}`,
													...(outputRatio >= 1
														? { width: "100%" }
														: { height: "min(28rem, 72vw)" }),
												}}
											>
												<div
													className="absolute top-1/2 left-1/2 max-w-none"
													style={{
														width: isQuarterTurn
															? `${100 / outputRatio}%`
															: "100%",
														height: isQuarterTurn
															? `${outputRatio * 100}%`
															: "100%",
														transform: "translate(-50%, -50%)",
													}}
												>
													{/* User-selected local videos do not include a page-provided captions track. */}
													{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
													<video
														ref={videoRef}
														src={videoUrl}
														controls
														preload="metadata"
														className="size-full object-contain transition-transform duration-200 motion-reduce:transition-none"
														style={{ transform: previewTransform }}
														onLoadedMetadata={handleMetadata}
														onError={handleVideoError}
													/>
												</div>
											</div>
										) : null}
									</div>

									{phase === "ready" ||
									phase === "processing" ||
									phase === "success" ||
									phase === "failed" ? (
										<div className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3 text-center text-xs sm:grid-cols-4 dark:bg-slate-950">
											<div>
												<span className="text-muted-foreground block">
													{t("metrics.original")}
												</span>
												<strong className="mt-1 block">
													{sourceSize.width}×{sourceSize.height}
												</strong>
											</div>
											<div>
												<span className="text-muted-foreground block">
													{t("metrics.after")}
												</span>
												<strong className="mt-1 block">
													{outputSize.width}×{outputSize.height}
												</strong>
											</div>
											<div>
												<span className="text-muted-foreground block">
													{t("metrics.orientation")}
												</span>
												<strong className="mt-1 block">
													{t(`orientation.${getOrientationKey(outputSize)}`)}
												</strong>
											</div>
											<div>
												<span className="text-muted-foreground block">
													{t("metrics.audio")}
												</span>
												<strong className="mt-1 block">
													{keepAudio ? t("metrics.keep") : t("metrics.remove")}
												</strong>
											</div>
										</div>
									) : null}
								</div>
							</div>

							<aside className="space-y-4" aria-label={t("aria.controls")}>
								<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900 sm:p-6">
									<div className="flex items-center justify-between gap-3">
										<div>
											<p className="text-xs font-bold tracking-[0.16em] text-blue-700 uppercase dark:text-cyan-300">
												{t("controls.eyebrow")}
											</p>
											<h3 className="mt-1 text-xl font-bold tracking-tight">
												{t("controls.title")}
											</h3>
										</div>
										<span className="rounded-full bg-blue-50 px-3 py-1.5 text-sm font-bold text-blue-800 dark:bg-blue-950/70 dark:text-cyan-200">
											{rotation}°
										</span>
									</div>

									<div className="mt-5 grid grid-cols-2 gap-2.5">
										<Button
											variant="outline"
											className="h-auto min-h-12 rounded-xl bg-slate-50 whitespace-normal dark:bg-slate-950"
											onClick={() => {
												setRotation(wrapRotation(rotation + 270));
											}}
										>
											<RotateCcw aria-hidden="true" /> {t("controls.left")}
										</Button>
										<Button
											variant="outline"
											className="h-auto min-h-12 rounded-xl bg-slate-50 whitespace-normal dark:bg-slate-950"
											onClick={() => {
												setRotation(wrapRotation(rotation + 90));
											}}
										>
											<RotateCw aria-hidden="true" /> {t("controls.right")}
										</Button>
										<Button
											variant="outline"
											className="h-auto min-h-12 rounded-xl bg-slate-50 whitespace-normal dark:bg-slate-950"
											onClick={() => {
												setRotation(wrapRotation(rotation + 180));
											}}
										>
											<RefreshCcw aria-hidden="true" /> {t("controls.turn180")}
										</Button>
										<Button
											variant="ghost"
											className="h-auto min-h-12 rounded-xl whitespace-normal"
											disabled={!hasTransform}
											title={
												!hasTransform
													? t("controls.resetUnavailable")
													: undefined
											}
											onClick={resetTransform}
										>
											{t("controls.reset")}
										</Button>
									</div>

									<div className="mt-6 border-t pt-5">
										<p className="text-sm font-semibold">
											{t("controls.flipTitle")}
										</p>
										<div className="mt-3 grid grid-cols-2 gap-2.5">
											<Button
												variant="outline"
												className={cn(
													"h-auto min-h-12 rounded-xl whitespace-normal",
													flipHorizontal &&
														"border-blue-500 bg-blue-50 text-blue-950 ring-1 ring-blue-500/20 dark:border-cyan-400 dark:bg-cyan-950/40 dark:text-cyan-100",
												)}
												aria-pressed={flipHorizontal}
												onClick={() => {
													setFlipHorizontal(!flipHorizontal);
												}}
											>
												<FlipHorizontal2 aria-hidden="true" />{" "}
												{t("controls.horizontal")}
												{flipHorizontal ? (
													<Check className="ms-auto" aria-hidden="true" />
												) : null}
											</Button>
											<Button
												variant="outline"
												className={cn(
													"h-auto min-h-12 rounded-xl whitespace-normal",
													flipVertical &&
														"border-blue-500 bg-blue-50 text-blue-950 ring-1 ring-blue-500/20 dark:border-cyan-400 dark:bg-cyan-950/40 dark:text-cyan-100",
												)}
												aria-pressed={flipVertical}
												onClick={() => {
													setFlipVertical(!flipVertical);
												}}
											>
												<FlipVertical2 aria-hidden="true" />{" "}
												{t("controls.vertical")}
												{flipVertical ? (
													<Check className="ms-auto" aria-hidden="true" />
												) : null}
											</Button>
										</div>
									</div>
								</div>

								<div className="rounded-2xl border border-cyan-100 bg-cyan-50/70 p-5 dark:border-cyan-950 dark:bg-cyan-950/20">
									<div className="flex items-start gap-3">
										<Info
											className="mt-0.5 size-4.5 shrink-0 text-cyan-700 dark:text-cyan-300"
											aria-hidden="true"
										/>
										<div>
											<p className="text-sm font-semibold">
												{t("summary.title")}
											</p>
											<p className="text-muted-foreground mt-1 text-sm leading-6">
												{t("summary.description", {
													summary: transformSummary,
												})}
											</p>
										</div>
									</div>
								</div>

								{phase === "ready" ? (
									<>
										<div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm dark:border-blue-950 dark:bg-slate-900">
											<div className="mb-4">
												<p className="text-sm font-semibold">
													{t("export.format")}
												</p>
												<div className="mt-3 grid grid-cols-2 gap-2.5">
													{OUTPUT_FORMATS.map((formatOption) => (
														<Button
															key={formatOption}
															variant="outline"
															className={cn(
																"h-12 rounded-xl",
																format === formatOption &&
																	"border-blue-500 bg-blue-50 text-blue-950 ring-1 ring-blue-500/20 dark:border-cyan-400 dark:bg-cyan-950/40 dark:text-cyan-100",
															)}
															aria-pressed={format === formatOption}
															onClick={() => setFormat(formatOption)}
														>
															{formatOption}
															{format === formatOption ? (
																<Check className="ms-auto" aria-hidden="true" />
															) : null}
														</Button>
													))}
												</div>
											</div>
											<div className="mb-4">
												<p className="text-sm font-semibold">
													{t("export.quality")}
												</p>
												<div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
													{OUTPUT_QUALITIES.map((qualityOption) => (
														<Button
															key={qualityOption}
															variant="outline"
															className={cn(
																"h-auto min-h-12 rounded-xl px-3 whitespace-normal",
																quality === qualityOption &&
																	"border-blue-500 bg-blue-50 text-blue-950 ring-1 ring-blue-500/20 dark:border-cyan-400 dark:bg-cyan-950/40 dark:text-cyan-100",
															)}
															aria-pressed={quality === qualityOption}
															onClick={() => setQuality(qualityOption)}
														>
															{qualityOption === "Balanced"
																? t("export.balanced")
																: qualityOption === "High"
																	? t("export.high")
																	: t("export.smaller")}
															{quality === qualityOption ? (
																<Check className="ms-auto" aria-hidden="true" />
															) : null}
														</Button>
													))}
												</div>
											</div>
											<div className="flex items-center justify-between">
												<span className="text-sm font-semibold">
													{t("export.keepAudio")}
												</span>
												<Button
													variant="ghost"
													className="h-10 rounded-lg px-3"
													aria-pressed={keepAudio}
													onClick={() => setKeepAudio(!keepAudio)}
												>
													{keepAudio ? (
														<Check
															className="text-blue-700 dark:text-cyan-300"
															aria-hidden="true"
														/>
													) : null}
													<span className="ms-2">
														{keepAudio ? t("export.on") : t("export.off")}
													</span>
												</Button>
											</div>
										</div>

										<Button
											ref={exportButtonRef}
											className="h-12 w-full rounded-xl bg-blue-600 text-base text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 dark:bg-cyan-400 dark:text-slate-950 dark:hover:bg-cyan-300"
											disabled={phase !== "ready"}
											onClick={startRotate}
										>
											{t("export.button")}
										</Button>
									</>
								) : null}

								{phase === "processing" ? (
									<div
										className="rounded-2xl border border-blue-200 bg-blue-50 p-5 text-blue-950 dark:border-blue-800 dark:bg-blue-950/45 dark:text-blue-100"
										role="status"
										aria-live="polite"
									>
										<div className="flex flex-col items-center gap-3 text-center">
											<LoaderCircle
												className="size-6 animate-spin motion-reduce:animate-none"
												aria-hidden="true"
											/>
											<p className="font-semibold">{t("processing.title")}</p>
											<p className="text-sm">
												{t("processing.progress", { progress })}
											</p>
										</div>
									</div>
								) : null}

								{phase === "success" ? (
									<div
										className="rounded-2xl border border-green-200 bg-green-50 p-5 text-green-950 dark:border-green-800 dark:bg-green-950/45 dark:text-green-100"
										role="status"
										aria-live="polite"
									>
										<div className="flex flex-col gap-4">
											<div>
												<p className="font-semibold">{t("success.title")}</p>
												<p className="mt-1 text-sm leading-6">
													{t("success.description")}
													{resultBlob
														? ` ${t("success.size", { size: formatBytes({ bytes: resultBlob.size, locale }) })}`
														: ""}
												</p>
											</div>
											<div className="grid grid-cols-2 gap-2.5">
												<Button
													className="h-12 rounded-xl bg-green-600 text-white hover:bg-green-500 dark:bg-green-500 dark:text-slate-950"
													onClick={downloadResult}
												>
													<Download className="size-4" aria-hidden="true" />
													{t("success.download")}
												</Button>
												<Button
													variant="outline"
													className="h-12 rounded-xl"
													onClick={resetToEditor}
												>
													{t("success.adjust")}
												</Button>
											</div>
										</div>
									</div>
								) : null}

								{phase === "failed" ? (
									<div
										className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-950 dark:border-red-800 dark:bg-red-950/45 dark:text-red-100"
										role="alert"
										aria-live="polite"
									>
										<div className="flex items-start gap-3">
											<AlertTriangle
												className="mt-0.5 size-5 shrink-0"
												aria-hidden="true"
											/>
											<div className="flex flex-col gap-3">
												<p className="font-semibold">{t("failure.title")}</p>
												<p className="text-sm leading-6">
													{processingError || t("failure.unexpected")}
												</p>
												<Button
													variant="outline"
													className="mt-1 h-11 rounded-xl"
													onClick={resetToEditor}
												>
													{t("failure.retry")}
												</Button>
											</div>
										</div>
									</div>
								) : null}
							</aside>
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
